// src/components/leaderboard/LeaderboardTable.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { UserStats } from '@/types';
import { Trophy, Medal, Award } from 'lucide-react';

interface LeaderboardTableProps {
  users: UserStats[];
  currentUserId?: string;
}

export const LeaderboardTable: React.FC<LeaderboardTableProps> = ({ users, currentUserId }) => {
  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="text-lg font-bold text-muted-foreground">#{rank}</span>;
    }
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'from-yellow-500/20 to-amber-500/20 border-yellow-500/50';
      case 2:
        return 'from-gray-400/20 to-gray-500/20 border-gray-400/50';
      case 3:
        return 'from-amber-600/20 to-orange-500/20 border-amber-600/50';
      default:
        return 'from-muted/50 to-muted/30 border-border';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          Leaderboard
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {users.map((user, index) => {
            const rank = index + 1;
            const isCurrentUser = user.userId === currentUserId;

            return (
              <motion.div
                key={user.userId}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.05 }}
                className={`flex items-center gap-4 p-4 rounded-lg border-2 bg-gradient-to-r ${getRankColor(
                  rank
                )} ${isCurrentUser ? 'ring-2 ring-primary' : ''}`}
              >
                <div className="flex items-center justify-center w-12">
                  {getRankIcon(rank)}
                </div>

                <Avatar className="h-12 w-12">
                  <AvatarImage src={user.userPhoto} alt={user.userName} />
                  <AvatarFallback>{user.userName.charAt(0)}</AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold truncate">
                      {user.userName}
                      {isCurrentUser && (
                        <span className="ml-2 text-xs text-primary">(You)</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs">
                      🔥 {user.currentStreak} days
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      Total: {user.totalDays}
                    </Badge>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">{user.currentStreak}</div>
                  <div className="text-xs text-muted-foreground">streak</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

